import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * EL TEXTO DE ATHENA, CON FORMA (tanda 6). Athena escribe con `**negritas**`, listas con `- ` o
 * `1. ` y, a veces, un enlace. Esto lo pinta con elementos de React, nunca con HTML crudo
 * (`dangerouslySetInnerHTML`): lo que no sea una de esas cuatro cosas sale como texto, tal
 * cual, y React lo escapa. Un `<script>` se lee `<script>`; un `javascript:` no es un enlace.
 *
 * PURO: mismo texto, mismo resultado. Se prueba con `renderToStaticMarkup`.
 */

type Bloque =
  | { tipo: 'parrafo'; lineas: string[] }
  | { tipo: 'lista'; ordenada: boolean; desde: number; items: string[] };

const VINETA = /^\s*-\s+(.*)$/;
const NUMERO = /^\s*(\d{1,3})[.)]\s+(.*)$/;

/** El texto en párrafos y listas. Una línea en blanco separa párrafos; una lista termina donde deja de serlo. */
function enBloques(texto: string): Bloque[] {
  const bloques: Bloque[] = [];
  // Tras una línea en blanco no se sigue el bloque anterior: se abre otro.
  let corte = true;
  for (const linea of texto.replace(/\r\n?/g, '\n').split('\n')) {
    if (!linea.trim()) {
      corte = true;
      continue;
    }
    const ultimo = corte ? undefined : bloques[bloques.length - 1];
    corte = false;
    const vineta = VINETA.exec(linea);
    const numero = vineta ? null : NUMERO.exec(linea);
    if (vineta || numero) {
      const ordenada = Boolean(numero);
      const item = (vineta ? vineta[1] : numero?.[2]) ?? '';
      if (ultimo?.tipo === 'lista' && ultimo.ordenada === ordenada) ultimo.items.push(item);
      else bloques.push({ tipo: 'lista', ordenada, desde: numero ? Number(numero[1]) : 1, items: [item] });
    } else if (ultimo?.tipo === 'parrafo') ultimo.lineas.push(linea);
    else bloques.push({ tipo: 'parrafo', lineas: [linea] });
  }
  return bloques;
}

// Solo http(s), y escrito entero: nada de adivinar un «www.» ni otros esquemas.
const ENLACE = /(https?:\/\/[^\s<>"'`]+)/g;
// La puntuación con la que termina la frase no es parte del enlace.
const COLA = /[.,;:!?)\]}»”'"]+$/;

function conEnlaces(texto: string, clave: string): ReactNode[] {
  return texto.split(ENLACE).flatMap((parte, i): ReactNode[] => {
    if (i % 2 === 0) return parte ? [parte] : [];
    const cola = COLA.exec(parte)?.[0] ?? '';
    const url = cola ? parte.slice(0, -cola.length) : parte;
    return [
      <a
        key={`${clave}-${i}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-brand-700 underline decoration-brand-300 underline-offset-2 hover:decoration-brand-600 dark:text-brand-300 dark:decoration-brand-700"
      >
        {url}
      </a>,
      ...(cola ? [cola] : []),
    ];
  });
}

// Pegada a la palabra por los dos lados: «2 ** 3 ** 4» no es una negrita. (Sin «lookbehind»:
// un Safari viejo no lo entiende y se cae la página entera.)
const NEGRITA = /(\*\*[^\s*](?:[^*\n]*[^\s*])?\*\*)/g;

/** Una línea: sus negritas y sus enlaces. */
function enLinea(texto: string, clave: string): ReactNode[] {
  return texto.split(NEGRITA).flatMap((parte, i): ReactNode[] => {
    if (i % 2 === 0) return conEnlaces(parte, `${clave}-${i}`);
    return [
      <strong key={`${clave}-${i}`} className="font-semibold">
        {conEnlaces(parte.slice(2, -2), `${clave}-${i}`)}
      </strong>,
    ];
  });
}

export function TextoRico({ texto, className }: { texto: string; className?: string }) {
  const bloques = enBloques(texto);
  return (
    // `overflow-wrap: anywhere`: un enlace o un nombre de archivo largo no ensancha la burbuja en el teléfono.
    <div className={cn('space-y-2 [overflow-wrap:anywhere]', className)}>
      {bloques.map((b, i) => {
        if (b.tipo === 'parrafo') {
          return (
            <p key={i}>
              {b.lineas.flatMap((linea, j) => [...(j ? [<br key={`br-${j}`} />] : []), ...enLinea(linea, `${i}-${j}`)])}
            </p>
          );
        }
        const items = b.items.map((item, j) => (
          <li key={j} className="pl-0.5">
            {enLinea(item, `${i}-${j}`)}
          </li>
        ));
        return b.ordenada ? (
          <ol key={i} start={b.desde} className="list-decimal space-y-1 pl-5 marker:text-slate-400">
            {items}
          </ol>
        ) : (
          <ul key={i} className="list-disc space-y-1 pl-5 marker:text-brand-400">
            {items}
          </ul>
        );
      })}
    </div>
  );
}
